import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getSeasonT } from '../i18n/seasons';
import { ArrowLeft, Phone, Mail, MapPin, Shield, CreditCard, Clock, Star, Loader2 } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import LoyaltyCheck from '../components/LoyaltyCheck';
import RoomBasket from '../components/RoomBasket';
import BookingLeadCapture from '../components/BookingLeadCapture';
import PriceHeatmap from '../components/PriceHeatmap';
import { buildHourRows } from '../i18n/intros';
import { trackDeal } from '../components/rooms/dealCode';
import { BookingTrustBar, BookingTrustCards } from '../components/BookingTrust';

// Beds24 booking-page languages (sq is not offered by Beds24 → English); must be enabled in Beds24 → Booking engine → Internationalisation.
const BEDS24_LANG = { en: 'en', de: 'de', ru: 'ru', pl: 'pl', hu: 'hu', me: 'sr' };
// Beds24 loads our branded stylesheet (frontend/public/beds24-booking.css) into the booking page; must be HTTPS.
const CSS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/beds24-booking.css`; // base CSS + admin-edited texts + live early-booker ribbon

const BookingPage = () => {
  const { hotelInfo, rooms } = useHotel();
  const { lang } = useLanguage();
  const hourRows = buildHourRows(lang, hotelInfo.hours_active || hotelInfo.hours);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [basketUrl, setBasketUrl] = useState(null);
  // "Start over": drop the pre-selected room and the Beds24 basket session, reload the engine from the room list — works at any step.
  const [startedOver, setStartedOver] = useState(false);
  const [resetCount, setResetCount] = useState(0);
  const startOver = () => { setLoading(true); setBasketUrl(null); setStartedOver(true); setResetCount((n) => n + 1); setTimeout(() => frameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); };
  const frameRef = React.useRef(null);
  
  // Get booking parameters from URL or localStorage
  const checkIn = searchParams.get('checkin') || '';
  const checkOut = searchParams.get('checkout') || '';
  const adults = searchParams.get('adults') || '2';
  const children = searchParams.get('children') || '0';
  const coupon = (searchParams.get('coupon') || '').toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 30);
  const roomId = searchParams.get('roomId') || '';
  const voucher = (searchParams.get('voucher') || '').toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 30);
  
  // Build Beds24 iframe URL with hotel branding parameters
  const buildBeds24Url = () => {
    let url = `https://www.beds24.com/booking2.php?propid=${hotelInfo.beds24Id}`;
    
    // Hide Beds24 branding elements for cleaner look
    url += '&hidealiases=1';
    url += '&hidehead=1';
    url += '&hidefooter=1';
    url += '&hidepropdesc=1';
    url += '&hidepropname=1';
    
    // Pre-fill dates if provided
    if (checkIn) url += `&checkin=${checkIn}`;
    if (checkOut) url += `&checkout=${checkOut}`;
    url += `&numadult=${adults}`;
    if (parseInt(children) > 0) url += `&numchild=${children}`;
    
    // Beds24 only accepts ITS room ids — map a local id (1, 2, …) to beds24Id; unknown ids are dropped (they caused "SORRY 404").
    if (roomId && !startedOver) {
      const wanted = String(roomId);
      const match = (rooms || []).find((r) => String(r.id) === wanted || String(r.beds24Id) === wanted);
      const b24 = match ? match.beds24Id : (wanted.length >= 5 ? wanted : null);
      if (b24) url += `&roomid=${b24}`;
    }
    if (voucher) url += `&voucher=${voucher}`;
    if (BEDS24_LANG[lang]) url += `&lang=${BEDS24_LANG[lang]}`;
    if (CSS_URL.startsWith('https://')) url += `&cssfile=${encodeURIComponent(CSS_URL)}`;
    
    return url;
  };

  useEffect(() => { if (voucher) trackDeal(voucher, 'checkout'); }, [voucher]);

  useEffect(() => {
    // Set loading false after iframe loads
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
      {/* Custom Header - La Rosa Negra Branding */}
      <header className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-orange-500/30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Back Button & Logo */}
            <div className="flex items-center gap-4">
              <Link 
                to="/#rooms" 
                className="text-white/70 hover:text-white flex items-center gap-2 transition-colors"
                data-testid="back-to-home"
              >
                <ArrowLeft size={20} />
                <span className="hidden sm:inline">Back to Rooms</span>
              </Link>
              
              <div className="h-8 w-px bg-white/20 hidden sm:block" />
              
              <div className="flex items-center gap-3">
                <img
                  src="/logo.png"
                  alt="La Rosa Negra"
                  className="w-10 h-10 rounded-lg object-cover shadow-lg"
                />
                <div className="hidden md:block">
                  <h1 className="font-bold text-white text-lg">{hotelInfo.name}</h1>
                  <p className="text-xs text-orange-400">Secure Booking</p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="hidden lg:flex items-center gap-6 text-sm">
              <a href={`tel:${hotelInfo.phone}`} className="text-white/70 hover:text-white flex items-center gap-2 transition-colors">
                <Phone size={16} className="text-orange-400" />
                {hotelInfo.phone}
              </a>
              <a href={`mailto:${hotelInfo.email}`} className="text-white/70 hover:text-white flex items-center gap-2 transition-colors">
                <Mail size={16} className="text-orange-400" />
                {hotelInfo.email}
              </a>
            </div>

            {/* Trust Badge */}
            <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1.5 rounded-full">
              <Shield size={16} className="text-green-400" />
              <span className="text-green-400 text-xs font-medium">Secure</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          
          {/* Sidebar - Hotel Info & Trust */}
          <div className="lg:col-span-1 space-y-4">
            <BookingLeadCapture />
            {/* Hotel Card */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-5 border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://hotelsinulcinj.com/images/bookingengine/r/36/70e5bc846d6a7814b25d8a43cea9b1b6.jpg" 
                  alt={hotelInfo.name}
                  className="w-16 h-16 rounded-xl object-cover"
                />
                <div>
                  <h3 className="font-bold text-white">{hotelInfo.name}</h3>
                  <div className="flex items-center gap-1 mt-1">
                    {[...Array(3)].map((_, i) => (
                      <Star key={i} size={12} className="text-orange-400 fill-orange-400" />
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-2 text-white/60 text-sm">
                <MapPin size={16} className="text-orange-400 flex-shrink-0 mt-0.5" />
                <span>{hotelInfo.location}</span>
              </div>
              
              {/* Rating Badge */}
              <div className="mt-4 flex items-center gap-3">
                <div className="bg-orange-500 text-white px-2.5 py-1 rounded font-bold text-sm">
                  {hotelInfo.rating}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Very Good</p>
                  <p className="text-white/50 text-xs">{hotelInfo.reviewCount} reviews</p>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-white/5 backdrop-blur rounded-2xl p-5 border border-white/10 space-y-4">
              <h4 className="font-semibold text-white flex items-center gap-2">
                <Shield size={18} className="text-green-400" />
                Booking Guarantee
              </h4>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CreditCard size={16} className="text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Secure Payment</p>
                    <p className="text-white/50 text-xs">SSL encrypted checkout</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock size={16} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Free Cancellation</p>
                    <p className="text-white/50 text-xs">Up to 24h before check-in</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star size={16} className="text-orange-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Best Price Guarantee</p>
                    <p className="text-white/50 text-xs">Book direct & save</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Need Help Card */}
            <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-2xl p-5 border border-orange-500/30">
              <h4 className="font-semibold text-white mb-3">Need Help?</h4>
              <p className="text-white/70 text-sm mb-4">Our team is here to assist you with your booking.</p>
              
              <div className="space-y-2">
                <a 
                  href={`tel:${hotelInfo.phone}`}
                  className="flex items-center gap-2 text-orange-400 hover:text-orange-300 text-sm transition-colors"
                >
                  <Phone size={14} />
                  {hotelInfo.phone}
                </a>
                <a 
                  href={`https://wa.me/${hotelInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-green-400 hover:text-green-300 text-sm transition-colors"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>

          {/* Main Booking Frame */}
          <div className="lg:col-span-3">
            {/* Selected Dates Banner */}
            {checkIn && checkOut && (
              <div className="bg-orange-500/20 border border-orange-500/40 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-orange-300 text-xs uppercase">Check-in</p>
                    <p className="text-white font-bold">
                      {new Date(checkIn).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="text-orange-400 px-2">→</div>
                  <div className="text-center">
                    <p className="text-orange-300 text-xs uppercase">Check-out</p>
                    <p className="text-white font-bold">
                      {new Date(checkOut).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="text-white/70 text-sm">
                  {adults} adult{adults !== '1' ? 's' : ''}{parseInt(children) > 0 ? `, ${children} child${children !== '1' ? 'ren' : ''}` : ''}
                </div>
              </div>
            )}
            {searchParams.get('hold') && (
              <div className="mb-4 inline-flex flex-wrap items-center gap-2 rounded-xl border border-emerald-400/60 bg-emerald-500/15 px-4 py-2 text-sm text-emerald-100" data-testid="booking-hold-banner">
                <span className="font-semibold">Your 24 h price hold is active</span><span>— finish the booking below at the price we quoted; reception has your request.</span>
              </div>
            )}
            <LoyaltyCheck lang={lang} apiUrl={process.env.REACT_APP_BACKEND_URL} />
            {coupon && (
              <div className="mb-4 inline-flex flex-wrap items-center gap-2 rounded-xl border border-orange-400/60 bg-orange-500/15 px-4 py-2 text-sm text-white" data-testid="booking-coupon-banner">
                <span className="font-semibold text-orange-300">{getSeasonT(lang).dealCode}:</span>
                <code className="font-mono font-bold tracking-wider bg-white/10 rounded px-2 py-0.5" data-testid="booking-coupon-code">{coupon}</code>
                <span className="text-white/70 text-xs">{getSeasonT(lang).couponNote}</span>
              </div>
            )}
            {voucher && (
              <div className="mb-4 inline-flex flex-wrap items-center gap-2 rounded-xl border border-emerald-400/60 bg-emerald-500/15 px-4 py-2 text-sm text-white" data-testid="booking-voucher-banner">
                <span className="font-semibold text-emerald-300">Deal applied:</span>
                <code className="font-mono font-bold tracking-wider bg-white/10 rounded px-2 py-0.5" data-testid="booking-voucher-code">{voucher}</code>
                <span className="text-white/70 text-xs">The code is pre-filled in the voucher field below — the discounted price shows at checkout.</span>
              </div>
            )}

            <p className="text-white/60 text-xs mb-4 inline-flex items-center gap-1.5" data-testid="booking-hours-line">
              <Clock className="w-3.5 h-3.5 text-orange-400" />
              {hourRows[1].label} {hourRows[1].value} · {hourRows[2].label} {hourRows[2].value}
              {hotelInfo.hours_active?.season ? <span className="text-orange-300"> · {hotelInfo.hours_active.season}</span> : null}
            </p>
            
            {/* Booking Frame Container */}
            <PriceHeatmap />
            <BookingTrustBar lang={lang} bookingUrl={hotelInfo.bookingComUrl} />
            <RoomBasket
              lang={lang}
              apiUrl={process.env.REACT_APP_BACKEND_URL}
              propId={hotelInfo.beds24Id}
              rooms={rooms}
              initialCheckIn={checkIn || undefined}
              initialNights={checkIn && checkOut ? Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000)) : undefined}
              initialAdults={adults}
              checkoutActive={!!basketUrl}
              onCheckout={(u) => { setLoading(true); setBasketUrl(u); setTimeout(() => frameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); }}
              onReset={() => { if (basketUrl) { setLoading(true); setBasketUrl(null); } }}
            />
            <div ref={frameRef} className="bg-white rounded-2xl overflow-hidden shadow-2xl relative" style={{ minHeight: '700px' }}>
              {/* Loading Overlay */}
              {loading && (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
                  <div className="text-center">
                    <Loader2 size={40} className="animate-spin text-orange-500 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Loading booking options...</p>
                    <p className="text-gray-400 text-sm mt-1">Checking availability</p>
                  </div>
                </div>
              )}
              
              {/* Beds24 Iframe */}
              {!basketUrl && (
              <div className="flex items-center justify-between gap-3 px-4 py-2 bg-orange-50 border-b border-orange-100 text-sm" data-testid="booking-startover-bar">
                <span className="text-gray-600">Changed your mind? You can remove the selected room and pick again from the full list — even at the last step.</span>
                <button type="button" onClick={startOver} className="shrink-0 inline-flex items-center gap-1.5 rounded-full border-2 border-orange-500 text-orange-600 font-semibold px-4 py-1.5 hover:bg-orange-500 hover:text-white transition-colors" data-testid="booking-startover">Remove room & start over</button>
              </div>
              )}
              <iframe
                key={`${basketUrl || 'default'}-${resetCount}`}
                src={basketUrl || buildBeds24Url()}
                title="Book at Hotel La Rosa Negra"
                className="w-full border-0"
                style={{ height: '700px', minHeight: '700px' }}
                onLoad={() => setLoading(false)}
                data-testid="beds24-booking-iframe"
              />
            </div>

            {/* Guarantees + what happens after you book */}
            <BookingTrustCards lang={lang} context={{ room: ((rooms || []).find((r) => String(r.id) === String(roomId)) || {}).name, dates: checkIn && checkOut ? `${new Date(checkIn).toLocaleDateString('en-GB')} – ${new Date(checkOut).toLocaleDateString('en-GB')}` : '' }} />
            <div className="mt-6 flex flex-wrap items-center justify-center gap-8 text-white/50 text-xs">
              <div className="flex items-center gap-2">
                <Shield size={14} />
                <span>256-bit SSL Encryption</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard size={14} />
                <span>All Major Cards Accepted</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-auto border-t border-white/10 py-6">
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} {hotelInfo.name}. All rights reserved.
          </p>
          <p className="text-white/30 text-xs mt-1">
            {hotelInfo.address}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookingPage;
